// Transform a FHIR batch/transaction Bundle into the ResourceChangeSet[] the
// queued-edits UI renders. Handles every entry verb:
//
//   POST   (create) — diff an empty skeleton against the new resource
//   PUT    (update) — diff the current resource against the entry resource
//   PATCH  (update) — apply the patch ops to the current resource, then diff
//   DELETE (delete) — diff the current resource against an empty skeleton
//
// All diffing goes through the schema-aware `getResourceDiff` — callers must
// have the involved resource types' schemas loaded (`medplum.requestSchema`
// in the browser, `indexFhirDefinitions()` from medplum-definitions.test-utils
// in tests). PATCH entries
// support both wire formats: Binary JSON-patch and fhirpath-patch
// `Parameters` (applied via the true FHIRPath evaluator, so `where(...)`
// filters resolve correctly).

import { evalFhirPathTyped, type TypedValue, toTypedValue } from '@medplum/core'
import type {
  Bundle,
  BundleEntry,
  Parameters,
  Resource,
  ResourceType,
} from '@medplum/fhirtypes'
import { isEqual } from 'lodash-es'
import { applyPatch, type Operation } from 'rfc6902'
import { getDisplayString } from '#src/display.ts'
import { base64Decode } from '#src/encoding.ts'
import {
  applyFhirPathPatch,
  type FhirPathPatchOperation,
  fhirPathPatchToJsonPatch,
  getFhirPathPatchSidecars,
  isFhirPathPatchParameters,
  parametersToFhirPathPatchOperations,
} from '#src/fhirpath-patch/index.ts'
import type { JsonObject, JsonValue } from '#src/json.ts'
import { withoutProposalBookkeeping } from '#src/queued-edits/bookkeeping.ts'
import { getEntryGroup } from '#src/queued-edits/entry-groups.ts'
import { getEntryIntent } from '#src/queued-edits/entry-intents.ts'
import { resourceExpectedOwner } from '#src/queued-edits/expected-owner.ts'
import type {
  ArrayItem,
  ArrayItemStatus,
  FhirPathPatchOp,
  FieldChange,
  ResourceChangeSet,
  ScalarFieldChange,
} from '#src/queued-edits/model.ts'
import { entryChangedSinceProposed } from '#src/queued-edits/proposal-drift.ts'
import { getPatientReferenceFromResource } from '#src/references.ts'
import {
  getResourceDiff,
  type ResourceFieldDiff,
} from '#src/resource-diff/resource-diff.ts'

export interface BundleToChangeSetsInput {
  bundle: Bundle
  // Current server state for entries that mutate an existing resource, keyed by
  // `ResourceType/id`.
  current?: Record<string, Resource>
  // For conditional-create (POST + `ifNoneExist`) entries: whether a resource
  // matching the entry's predicate already exists, keyed by the entry's
  // fullUrl. Supplied by the caller (evaluating a search predicate needs the
  // server). When an entry's predicate matches, the create would silently
  // no-op at apply — surfaced as `alreadyApplied` so the review renders it as
  // a first-class "already exists" state instead of a change that looks like
  // it would happen.
  conditionalCreateExists?: Record<string, boolean>
  // The pinned base versions fhirpath-patch entries were drafted against,
  // keyed by each entry's `__seenBaseRef` history reference string (see
  // bundleBaseRefs). Enables the changed-since-proposed drift flag; omit to
  // skip that check.
  bases?: Record<string, Resource>
}

export function bundleToChangeSets({
  bundle,
  current,
  conditionalCreateExists = {},
  bases = {},
}: BundleToChangeSetsInput): ResourceChangeSet[] {
  const result: ResourceChangeSet[] = []

  const ctx: EntryContext = {
    current: current ?? {},
    // Distinguishes "the caller read current state and this target was gone"
    // (drift signal) from "no current state was supplied at all" (stories,
    // pre-computed flows) — only the former may classify a DELETE as already
    // applied.
    currentProvided: current !== undefined,
    conditionalCreateExists,
    bases,
  }
  for (const [index, entry] of (bundle.entry ?? []).entries()) {
    const built = entryToChangeSet(entry, index, ctx)
    if (built) {
      result.push(built)
    }
  }

  return result
}

// Every resource type a bundle (plus its current-state map) touches — the
// schemas a caller must load before computing change sets.
export function bundleResourceTypes(
  bundle: Bundle,
  current?: Record<string, Resource>
): ResourceType[] {
  const types = new Set<string>()
  for (const entry of bundle.entry ?? []) {
    const ref = referenceForEntry(entry)
    if (ref) {
      types.add(ref.split('/')[0])
    }
    const resourceType = entry.resource?.resourceType
    if (
      resourceType &&
      resourceType !== 'Binary' &&
      resourceType !== 'Parameters'
    ) {
      types.add(resourceType)
    }
  }
  for (const resource of Object.values(current ?? {})) {
    types.add(resource.resourceType)
  }
  return [...types] as ResourceType[]
}

interface EntryContext {
  current: Record<string, Resource>
  currentProvided: boolean
  conditionalCreateExists: Record<string, boolean>
  bases: Record<string, Resource>
}

function entryToChangeSet(
  entry: BundleEntry,
  index: number,
  ctx: EntryContext
): ResourceChangeSet | undefined {
  const method = entry.request?.method
  if (!method) {
    return undefined
  }

  const ref = referenceForEntry(entry)
  const [refType, resourceId] = splitRef(ref)
  const before = ref ? ctx.current[ref] : undefined

  // The entry's domain resource (PATCH payloads are Binary/Parameters
  // envelopes, not the resource being changed).
  const entryResource =
    entry.resource &&
    entry.resource.resourceType !== 'Binary' &&
    entry.resource.resourceType !== 'Parameters'
      ? entry.resource
      : undefined
  const resourceType = entryResource?.resourceType ?? refType

  // Label: getDisplayString of the current resource (update/delete) or the
  // resource being created (create), falling back to the resourceType.
  const labelResource = before ?? entryResource
  const derivedLabel = labelResource
    ? getDisplayString(labelResource)
    : undefined
  const common = {
    // Positional key: multiple no-id POSTs of the same type must get
    // distinct decision keys.
    id: `entry-${index}`,
    reference: ref,
    fullUrl: entry.fullUrl,
    resourceType,
    resourceId,
    patientReference: getPatientReferenceFromResource(labelResource)?.reference,
    label: derivedLabel || resourceType,
    intent: getEntryIntent(entry),
    group: getEntryGroup(entry),
    // Resolved from live current state when available (before ?? the entry's
    // own body for creates), so post-proposal ownership reassignments show.
    expectedOwner: labelResource
      ? resourceExpectedOwner(labelResource)
      : undefined,
    subtitle: ref ?? entry.fullUrl,
  }

  switch (method) {
    case 'POST': {
      const resource = entry.resource
      if (!resource) {
        return undefined
      }
      // A conditional create whose `ifNoneExist` predicate already matches a
      // resource would no-op at apply (the server returns the existing match
      // instead of creating a duplicate) — surface that instead of rendering a
      // create that will not happen.
      const alreadyExists = Boolean(
        entry.request?.ifNoneExist &&
        entry.fullUrl &&
        ctx.conditionalCreateExists[entry.fullUrl]
      )
      const created: ResourceChangeSet = {
        ...common,
        resource,
        verb: 'create',
        changes: diffChanges(skeleton(resource.resourceType), resource),
      }
      if (alreadyExists) {
        created.alreadyApplied = true
      }
      return created
    }

    case 'PUT': {
      const resource = entry.resource
      if (!resource) {
        return undefined
      }
      return {
        ...common,
        resource,
        verb: 'update',
        changes: diffChanges(
          before ?? skeleton(resource.resourceType),
          resource
        ),
      }
    }

    case 'PATCH': {
      // Field-staleness drift: a touched domain path moved between the pinned
      // draft base and current state — the proposer never saw the diff the
      // reviewer is looking at, so the card flags it for closer review.
      const drifted = entryChangedSinceProposed(entry, before, ctx.bases)
      // Only a drifted entry gets an Original column: otherwise the base value
      // and the Before value are the same thing on every row.
      const base = drifted ? entryBaseResource(entry, ctx.bases) : undefined
      const patched: ResourceChangeSet = {
        ...common,
        verb: 'update',
        ...patchPreview(entry, before, base),
      }
      if (drifted) {
        patched.changedSinceProposed = true
      }
      return patched
    }

    case 'DELETE': {
      // Target already absent: the delete's effect is already present (someone
      // removed the resource outside the review) — a safe execution no-op, but
      // wrong as an invisible review outcome, so it is surfaced and dropped.
      const alreadyAbsent = ctx.currentProvided && !before
      const deleted: ResourceChangeSet = {
        ...common,
        resource: before,
        verb: 'delete',
        changes: before
          ? diffChanges(before, skeleton(before.resourceType))
          : [],
      }
      if (alreadyAbsent) {
        deleted.alreadyApplied = true
      }
      return deleted
    }

    default: {
      return undefined
    }
  }
}

function skeleton(resourceType: string): Resource {
  return { resourceType } as Resource
}

// --- PATCH preview ----------------------------------------------------------

interface PatchPreview {
  resource?: Resource
  changes: FieldChange[]
  patchOps?: FhirPathPatchOp[]
  conversionError?: string
  alreadyApplied?: boolean
}

// `base` is the pinned version the proposal was drafted against, supplied only
// for drifted entries so each rendered row can also carry the value the
// proposer saw.
function patchPreview(
  entry: BundleEntry,
  before: Resource | undefined,
  base?: Resource
): PatchPreview {
  const payload = entry.resource
  const patchOps = displayPatchOps(payload)
  const semanticPatchOps =
    payload && isFhirPathPatchParameters(payload)
      ? parametersToFhirPathPatchOperations(payload)
      : []

  if (!before) {
    return {
      changes: [],
      patchOps,
      conversionError:
        'No current resource state available to preview this change.',
    }
  }

  let ops: Operation[]
  try {
    ops = extractJsonPatchOps(payload, before)
  } catch (error) {
    return {
      changes: [],
      patchOps,
      conversionError: error instanceof Error ? error.message : String(error),
    }
  }

  const revised = structuredClone(before)
  const errors = applyPatch(
    revised,
    // The version-test precondition is about apply-time safety, not the
    // change being proposed — skip it for the preview.
    ops.filter((op) => op.op !== 'test')
  )
  const failed = errors.find((e) => e !== null)
  if (failed) {
    return {
      changes: [],
      patchOps,
      conversionError: `Patch does not apply to the current resource: ${failed.message}`,
    }
  }

  // Bookkeeping (origin/batch tags, Task.lastModified) is excluded from both
  // the already-applied comparison AND the rendered diff — it is not a change
  // the reviewer is deciding on, and the lastModified value is the proposal
  // BUILD time (stale by construction, it would even render as time moving
  // backwards).
  const strippedBefore = withoutProposalBookkeeping(before)
  const strippedRevised = withoutProposalBookkeeping(revised)

  // The proposal's domain change may have landed outside the review since the
  // bundle was built (manual cleanup, another workflow). When applying the
  // patch changes nothing beyond bookkeeping, the entry is a no-op — surfaced
  // so the review surface can drop it instead of asking the reviewer to
  // approve nothing.
  const alreadyApplied = isEqual(strippedBefore, strippedRevised)
  const originalPaths = base
    ? semanticOriginalPaths(before, semanticPatchOps)
    : undefined

  const preview: PatchPreview = {
    resource: revised,
    changes: diffChanges(
      strippedBefore,
      strippedRevised,
      base && withoutProposalBookkeeping(base),
      originalPaths
    ),
    patchOps,
  }
  if (alreadyApplied) {
    preview.alreadyApplied = alreadyApplied
  }
  return preview
}

// The pinned base version an entry's fhirpath-patch was drafted against, when
// the caller supplied it (same lookup entryChangedSinceProposed runs).
function entryBaseResource(
  entry: BundleEntry,
  bases: Record<string, Resource>
): Resource | undefined {
  if (!isFhirPathPatchParameters(entry.resource)) {
    return undefined
  }
  const { baseRef } = getFhirPathPatchSidecars(entry.resource)
  return baseRef ? bases[baseRef] : undefined
}

// Whether applying a PATCH entry to `before` would change nothing beyond
// stamping the queued-edits origin tag — the same already-applied test the
// review preview runs, exposed for the apply-time rebase (see
// apply-rebase.ts). Conservative: any conversion or apply failure returns
// false, so an un-evaluable entry stays live and fails loudly at apply instead
// of being silently dropped.
export function patchEntryAlreadyApplied(
  entry: BundleEntry,
  before: Resource
): boolean {
  let ops: Operation[]
  try {
    ops = extractJsonPatchOps(entry.resource, before)
  } catch {
    return false
  }
  const revised = structuredClone(before)
  const errors = applyPatch(
    revised,
    ops.filter((op) => op.op !== 'test')
  )
  if (errors.some((e) => e !== null)) {
    return false
  }
  return isEqual(
    withoutProposalBookkeeping(before),
    withoutProposalBookkeeping(revised)
  )
}

// A bundle PATCH entry carries its ops either as a Binary (json-patch) or a
// fhirpath-patch Parameters resource. Normalize both to rfc6902 Operation[].
function extractJsonPatchOps(
  payload: Resource | undefined,
  before: Resource
): Operation[] {
  if (payload && isFhirPathPatchParameters(payload)) {
    return fhirPathPatchToJsonPatch(payload, before)
  }
  if (payload?.resourceType === 'Binary' && payload.data) {
    return JSON.parse(base64Decode(payload.data)) as Operation[]
  }
  throw new Error('Unrecognized PATCH payload — expected Binary or Parameters')
}

// Technical-panel rows for the raw patch operations.
function displayPatchOps(
  payload: Resource | undefined
): FhirPathPatchOp[] | undefined {
  if (payload && isFhirPathPatchParameters(payload)) {
    return parametersToFhirPathPatchOperations(payload as Parameters).map(
      (op) => ({
        op: op.type,
        fhirPath: op.path,
        value:
          op.value === undefined ? undefined : stringifyValue(op.value.value),
      })
    )
  }
  if (payload?.resourceType === 'Binary' && payload.data) {
    try {
      const ops = JSON.parse(base64Decode(payload.data)) as Operation[]
      return ops
        .filter((op) => op.op !== 'test')
        .map((op) => ({
          op: jsonOpToDisplayOpType(op.op),
          fhirPath: pointerToDottedPath(op.path),
          value:
            'value' in op && op.value !== undefined
              ? stringifyValue(op.value)
              : undefined,
        }))
    } catch {
      return undefined
    }
  }
  return undefined
}

function jsonOpToDisplayOpType(op: Operation['op']): FhirPathPatchOp['op'] {
  switch (op) {
    case 'add':
      return 'add'
    case 'remove':
      return 'delete'
    case 'move':
      return 'move'
    default:
      return 'replace'
  }
}

function pointerToDottedPath(pointer: string): string {
  return pointer
    .split('/')
    .filter(Boolean)
    .map((seg) => (/^\d+$/.test(seg) ? `[${seg}]` : seg))
    .join('.')
    .replaceAll('.[', '[')
}

// --- diff → FieldChange mapping ---------------------------------------------

// `base` is the pinned version a drifted proposal was drafted against; when
// supplied every row also carries the value it held there (`original`).
function diffChanges(
  original: Resource,
  revised: Resource,
  base?: Resource,
  originalPaths: ReadonlyMap<string, string> = new Map()
): FieldChange[] {
  const typedBase = base ? [toTypedValue(base)] : undefined
  return getResourceDiff({ original, revised }).map((diff) =>
    toFieldChange(diff, typedBase, originalPaths)
  )
}

function toFieldChange(
  diff: ResourceFieldDiff,
  typedBase: TypedValue[] | undefined,
  originalPaths: ReadonlyMap<string, string>
): FieldChange {
  const before = diff.originalValue?.value
  const after = diff.revisedValue?.value

  if (Array.isArray(before) || Array.isArray(after)) {
    // The diff path may name a position that does not line up at the base
    // version (`identifier.last()`, `note[2]`), so read the whole array there
    // and pair items by identity instead.
    const arrayPath = arrayRootPath(diff.fhirPath)
    const baseItems = typedBase ? asArray(evalAtBase(arrayPath, typedBase)) : []
    return {
      kind: 'array',
      label: arrayLabel(diff.fhirPath),
      fhirPath: diff.fhirPath,
      items: arrayItems(
        asArray(before),
        asArray(after),
        baseItems,
        arrayPath,
        arrayStartIndex(diff.fhirPath, arrayPath, originalPaths),
        typedBase,
        originalPaths
      ),
    }
  }

  const originalValue = typedBase
    ? evalAtBase(originalPathForDiff(diff.fhirPath, originalPaths), typedBase)
    : undefined

  const scalar: ScalarFieldChange = {
    kind: 'scalar',
    label: humanizePath(diff.fhirPath),
    before: before === undefined ? undefined : stringifyValue(before),
    after: after === undefined ? undefined : stringifyValue(after),
  }
  if (originalValue !== undefined) {
    scalar.original = stringifyValue(originalValue)
  }
  scalar.fhirPath = diff.fhirPath
  return scalar
}

interface OperationTarget {
  objectConcretePath: string
  objectSemanticPath: string
  suffix: string
}

function semanticOriginalPaths(
  resource: Resource,
  operations: FhirPathPatchOperation[]
): Map<string, string> {
  let baseObjectPaths = new Map<string, string>([['', resource.resourceType]])
  const touchedObjectPaths = new Map<string, string>()
  let working = structuredClone(resource)
  for (const operation of operations) {
    const target = concreteOperationTarget(operation, working)
    if (target) {
      const basePath =
        baseObjectPaths.get(target.objectConcretePath) ??
        target.objectSemanticPath
      touchedObjectPaths.set(target.objectConcretePath, basePath)
      baseObjectPaths.set(target.objectConcretePath, basePath)
    }
    const revised = applyFhirPathPatch(working, [operation])
    baseObjectPaths = rebaseObjectPaths(
      baseObjectPaths,
      working,
      revised,
      operation,
      target
    )
    working = revised
  }
  // Touch-time paths win: after a non-last delete, a later sibling shifts
  // into the vacated index and rebase would otherwise overwrite the deleted
  // item's provenance with the survivor's.
  return new Map([...baseObjectPaths, ...touchedObjectPaths])
}

function concreteOperationTarget(
  operation: FhirPathPatchOperation,
  resource: Resource
): OperationTarget | undefined {
  const typedResource = [toTypedValue(resource)]
  if (operation.type !== 'add') {
    const selected = evalFhirPathTyped(operation.path, typedResource)
    if (
      selected.length === 1 &&
      selected[0].value &&
      typeof selected[0].value === 'object'
    ) {
      const concretePath = findValuePath(resource, selected[0].value)
      return concretePath === undefined
        ? undefined
        : {
            objectConcretePath: concretePath,
            objectSemanticPath: operation.path,
            suffix: '',
          }
    }
  }
  const target =
    operation.type === 'add'
      ? { parentPath: operation.path, property: operation.name }
      : splitFhirPathProperty(operation.path)
  if (!target?.property) {
    return undefined
  }
  const parents = evalFhirPathTyped(target.parentPath, typedResource)
  if (parents.length !== 1) {
    return undefined
  }
  const parentPath = findValuePath(resource, parents[0].value)
  if (parentPath === undefined) {
    return undefined
  }
  const suffix = `.${target.property}`
  return {
    objectConcretePath: parentPath,
    objectSemanticPath: target.parentPath,
    suffix,
  }
}

function originalPathForDiff(
  path: string,
  baseObjectPaths: ReadonlyMap<string, string>
): string {
  const mapped = mappedOriginalPath(path, baseObjectPaths)
  if (mapped) {
    return mapped
  }
  const resourceType = baseObjectPaths.get('')
  return resourceType ? `${resourceType}.${path}` : path
}

function mappedOriginalPath(
  path: string,
  baseObjectPaths: ReadonlyMap<string, string>
): string | undefined {
  let match = ''
  for (const candidate of baseObjectPaths.keys()) {
    if (
      candidate.length > match.length &&
      (path === candidate ||
        path.startsWith(`${candidate}.`) ||
        path.startsWith(`${candidate}[`))
    ) {
      match = candidate
    }
  }
  if (!match) {
    return undefined
  }
  const basePath = baseObjectPaths.get(match)
  if (!basePath) {
    return undefined
  }
  return `${basePath}${path.slice(match.length)}`
}

function rebaseObjectPaths(
  paths: ReadonlyMap<string, string>,
  before: Resource,
  after: Resource,
  operation: FhirPathPatchOperation,
  target: OperationTarget | undefined
): Map<string, string> {
  const rebased = new Map<string, string>()
  for (const [path, basePath] of paths) {
    if (!path) {
      rebased.set(path, basePath)
      continue
    }
    const value = valueAtPath(before, path)
    const targetRemains =
      path === target?.objectConcretePath &&
      !(operation.type === 'delete' && target.suffix === '') &&
      valueAtPath(after, path) !== undefined
    if (targetRemains) {
      rebased.set(path, basePath)
      continue
    }
    if (equivalentObjects(valueAtPath(after, path), value)) {
      rebased.set(path, basePath)
      continue
    }
    const movedPath = findMovedValuePath(after, value, path)
    if (movedPath !== undefined) {
      rebased.set(movedPath, basePath)
    }
  }
  return rebased
}

function valueAtPath(
  value: Resource | JsonValue | undefined,
  path: string
): JsonValue | undefined {
  // SAFETY: callers pass FHIR resources and FHIRPath results, which are JSON-shaped.
  let current = value as JsonValue | undefined
  for (const match of path.matchAll(/([^.[]+)|\[(\d+)\]/g)) {
    if (current === undefined || current === null) {
      return undefined
    }
    current =
      match[2] === undefined
        ? (current as Record<string, JsonValue>)[match[1]]
        : (current as JsonValue[])[Number(match[2])]
  }
  return current
}

function equivalentObjects(
  value: JsonValue | undefined,
  target: JsonValue | undefined
): boolean {
  return Boolean(
    value &&
    target &&
    typeof value === 'object' &&
    typeof target === 'object' &&
    sameItem(value, target)
  )
}

function findMovedValuePath(
  resource: Resource,
  target: JsonValue | undefined,
  previousPath: string
): string | undefined {
  const arrayIndex = previousPath.lastIndexOf('[')
  if (arrayIndex === -1) {
    return undefined
  }
  const containerPath = previousPath.slice(0, arrayIndex)
  const container = valueAtPath(resource, containerPath)
  const relativePath = findEquivalentValuePath(container, target)
  if (relativePath === undefined) {
    return undefined
  }
  return `${containerPath}${relativePath}`
}

function findEquivalentValuePath(
  value: JsonValue | undefined,
  target: JsonValue | undefined,
  path = ''
): string | undefined {
  if (equivalentObjects(value, target)) {
    return path
  }
  if (Array.isArray(value)) {
    for (const [index, item] of value.entries()) {
      const found = findEquivalentValuePath(item, target, `${path}[${index}]`)
      if (found !== undefined) {
        return found
      }
    }
    return undefined
  }
  if (!value || typeof value !== 'object') {
    return undefined
  }
  for (const [key, child] of Object.entries(value)) {
    const childPath = path ? `${path}.${key}` : key
    const found = findEquivalentValuePath(child, target, childPath)
    if (found !== undefined) {
      return found
    }
  }
  return undefined
}

function splitFhirPathProperty(
  path: string
): { parentPath: string; property: string } | undefined {
  const separator = path.lastIndexOf('.')
  if (separator === -1) {
    return undefined
  }
  return {
    parentPath: path.slice(0, separator),
    property: path.slice(separator + 1),
  }
}

function findValuePath(
  value: Resource | JsonValue | undefined,
  target: JsonValue | undefined,
  path = ''
): string | undefined {
  if (value === target) {
    return path
  }
  if (Array.isArray(value)) {
    for (const [index, item] of value.entries()) {
      const found = findValuePath(item, target, `${path}[${index}]`)
      if (found !== undefined) {
        return found
      }
    }
    return undefined
  }
  if (!value || typeof value !== 'object') {
    return undefined
  }
  for (const [key, child] of Object.entries(value)) {
    const childPath = path ? `${path}.${key}` : key
    const found = findValuePath(child, target, childPath)
    if (found !== undefined) {
      return found
    }
  }
  return undefined
}

// Single FHIRPath value at the base version, or undefined when the path
// resolves to nothing there (the element did not exist yet) or fails to
// evaluate.
function evalAtBase(
  fhirPath: string,
  typedBase: TypedValue[]
): JsonValue | undefined {
  try {
    const values = evalFhirPathTyped(fhirPath, typedBase)
    if (values.length === 0) {
      return undefined
    }
    return values.length === 1 ? values[0].value : values.map((v) => v.value)
  } catch {
    return undefined
  }
}

// Strip a trailing positional segment so an array diff path names the array
// itself.
function arrayRootPath(fhirPath: string): string {
  return fhirPath.replace(/(\.last\(\)|\[\d+\])$/, '')
}

function arrayStartIndex(
  fhirPath: string,
  arrayPath: string,
  originalPaths: ReadonlyMap<string, string>
): number {
  const match = /\[(\d+)\]$/.exec(fhirPath)
  if (match) {
    return Number(match[1])
  }
  if (fhirPath.endsWith('.last()')) {
    const prefix = `${arrayPath}[`
    for (const [concretePath, semanticPath] of originalPaths) {
      if (
        concretePath.startsWith(prefix) &&
        semanticPath.endsWith(`.${fhirPath}`)
      ) {
        const index = concretePath.slice(prefix.length, -1)
        if (/^\d+$/.test(index)) {
          return Number(index)
        }
      }
    }
  }
  return 0
}

// Pair array items across before/after by identity (url / system+code /
// system+value), falling back to deep equality, so reordering does not read
// as remove + add and modified items show as one row.
function arrayItems(
  before: JsonValue[],
  after: JsonValue[],
  base: JsonValue[],
  arrayPath: string,
  beforeIndexOffset: number,
  typedBase: TypedValue[] | undefined,
  originalPaths: ReadonlyMap<string, string>
): ArrayItem[] {
  interface PendingArrayItem {
    item: JsonValue
    matchItem: JsonValue
    status: ArrayItemStatus
    beforeIndex?: number
  }

  const beforeToAfter = matchArrayItems(before, after)
  const matchedAfter = new Set(beforeToAfter.values())
  const pending: PendingArrayItem[] = []
  for (const [index, beforeItem] of before.entries()) {
    const afterIndex = beforeToAfter.get(index)
    if (afterIndex === undefined) {
      pending.push({
        item: beforeItem,
        matchItem: beforeItem,
        status: 'removed',
        beforeIndex: index,
      })
      continue
    }
    const afterItem = after[afterIndex]
    pending.push({
      item: afterItem,
      matchItem: beforeItem,
      status: isEqual(beforeItem, afterItem) ? 'unchanged' : 'modified',
      beforeIndex: index,
    })
  }
  for (const [index, afterItem] of after.entries()) {
    if (!matchedAfter.has(index)) {
      pending.push({
        item: afterItem,
        matchItem: afterItem,
        status: 'added',
      })
    }
  }

  const pendingToBase = new Map<number, number>()
  const matchedBase = new Set<number>()
  if (typedBase) {
    for (const [index, item] of pending.entries()) {
      if (item.beforeIndex === undefined) {
        continue
      }
      const semanticPath = mappedOriginalPath(
        `${arrayPath}[${beforeIndexOffset + item.beforeIndex}]`,
        originalPaths
      )
      const original = semanticPath
        ? evalAtBase(semanticPath, typedBase)
        : undefined
      const baseIndex = base.findIndex(
        (baseItem, candidateIndex) =>
          !matchedBase.has(candidateIndex) &&
          (baseItem === original || isEqual(baseItem, original))
      )
      if (baseIndex !== -1) {
        pendingToBase.set(index, baseIndex)
        matchedBase.add(baseIndex)
      }
    }
  }
  const unmatchedPending = [...pending.keys()].filter(
    (index) => !pendingToBase.has(index)
  )
  const unmatchedBase = [...base.keys()].filter(
    (index) => !matchedBase.has(index)
  )
  const fallbackMatches = matchArrayItems(
    unmatchedPending.map((index) => pending[index].matchItem),
    unmatchedBase.map((index) => base[index])
  )
  for (const [pendingIndex, baseIndex] of fallbackMatches) {
    pendingToBase.set(unmatchedPending[pendingIndex], unmatchedBase[baseIndex])
  }
  return pending.map(({ item, status }, index) =>
    buildArrayItem(item, status, base[pendingToBase.get(index) ?? -1])
  )
}

function matchArrayItems(
  source: unknown[],
  target: unknown[]
): Map<number, number> {
  const matches = new Map<number, number>()
  const matchedTargets = new Set<number>()
  for (const matchesItem of [isEqual, sameItem]) {
    for (const [sourceIndex, sourceItem] of source.entries()) {
      if (matches.has(sourceIndex)) {
        continue
      }
      const targetIndex = target.findIndex(
        (targetItem, index) =>
          !matchedTargets.has(index) && matchesItem(sourceItem, targetItem)
      )
      if (targetIndex !== -1) {
        matches.set(sourceIndex, targetIndex)
        matchedTargets.add(targetIndex)
      }
    }
  }
  return matches
}

function sameItem(a: JsonValue | undefined, b: JsonValue | undefined): boolean {
  const aKey = itemIdentity(a)
  const bKey = itemIdentity(b)
  if (aKey !== undefined || bKey !== undefined) {
    return aKey === bKey
  }
  return isEqual(a, b)
}

function itemIdentity(item: JsonValue | undefined): string | undefined {
  if (!item || typeof item !== 'object' || Array.isArray(item)) {
    return undefined
  }
  const o = item as JsonObject
  if (typeof o.url === 'string') {
    return `url:${o.url}`
  }
  if (typeof o.reference === 'string') {
    return `ref:${o.reference}`
  }
  if (typeof o.system === 'string' && typeof o.code === 'string') {
    return `sc:${o.system}|${o.code}`
  }
  if (typeof o.system === 'string' && typeof o.value === 'string') {
    return `sv:${o.system}|${o.value}`
  }
  return undefined
}

function buildArrayItem(
  item: JsonValue | undefined,
  status: ArrayItemStatus,
  baseMatch?: JsonValue
): ArrayItem {
  const o = item && typeof item === 'object' ? (item as JsonObject) : {}
  const arrayItem: ArrayItem = {
    status,
    primary: itemPrimaryLabel(item),
  }
  if (baseMatch !== undefined) {
    arrayItem.original = itemPrimaryLabel(baseMatch)
  }
  arrayItem.code = itemString(o.code) ?? itemString(o.value)
  arrayItem.system = itemString(o.system) ?? itemString(o.url)
  return arrayItem
}

function itemString(value: JsonValue | undefined): string | undefined {
  return value === undefined ? undefined : String(value)
}

function itemPrimaryLabel(item: JsonValue | undefined): string {
  if (item && typeof item === 'object') {
    const o = item as JsonObject
    const display = o.display ?? o.text
    if (typeof display === 'string') {
      return display
    }
    if (typeof o.value === 'string') {
      return o.value
    }
    if (typeof o.code === 'string') {
      return o.code
    }
    if (typeof o.url === 'string') {
      return shortUrl(o.url)
    }
  }
  return stringifyValue(item)
}

// --- value/label helpers ----------------------------------------------------

function asArray(value: JsonValue | undefined): JsonValue[] {
  return Array.isArray(value) ? value : value === undefined ? [] : [value]
}

function stringifyValue(value: JsonValue | undefined): string {
  if (value === undefined || value === null) {
    return ''
  }
  if (typeof value === 'string') {
    return value
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  if (typeof value === 'object') {
    const o = value as JsonObject
    const display = o.display ?? o.text ?? o.value ?? o.code
    if (typeof display === 'string') {
      return display
    }
    return JSON.stringify(value)
  }
  return String(value)
}

// For generic container arrays (`coding`), label by the parent segment
// (`type.coding` → "Type") so the row reads as the clinical concept.
function arrayLabel(fhirPath: string): string {
  const segments = fhirPath.split('.')
  const last = segments.at(-1) ?? fhirPath
  if (last === 'coding' && segments.length > 1) {
    return humanizePath(segments.at(-2) as string)
  }
  return humanizePath(last)
}

function humanizePath(path: string): string {
  // Drop trailing no-arg function segments (`note.last()` must read as
  // "Note", not "Last()").
  const segments = path.split('.')
  while (segments.length > 1 && /^\w+\(\)$/.test(segments.at(-1) ?? '')) {
    segments.pop()
  }
  const last = (segments.pop() ?? path).replace(/\[\d+\]/g, '')
  const spaced = last.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[._]/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function shortUrl(url: string): string {
  return url.split('/').pop() ?? url
}

// --- references --------------------------------------------------------------

function referenceForEntry(entry: BundleEntry): string | undefined {
  // Prefer the request URL (present for PUT/PATCH/DELETE and conditional POST).
  const url = entry.request?.url
  if (url && /^[A-Za-z]+\/[\w-]+$/.test(url)) {
    return url
  }
  const resource = entry.resource
  if (
    resource &&
    resource.resourceType !== 'Binary' &&
    resource.resourceType !== 'Parameters'
  ) {
    return resource.id ? `${resource.resourceType}/${resource.id}` : undefined
  }
  return undefined
}

function splitRef(ref: string | undefined): [string, string] {
  if (!ref) {
    return ['Resource', '']
  }
  const [type, id = ''] = ref.split('/')
  return [type, id]
}
